import * as React from 'react';
import { animated, useTransition } from '@react-spring/web';
import { DesktopBiddingConsoleExperiment } from '../DesktopBiddingConsoleExperiment/DesktopBiddingConsoleExperiment';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import { fetchItemDetailsIfNeeded, getItemDetailAgentId } from '@/redux/modules/itemDetail';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchListingAgentsIfNeeded } from '@/redux/modules/listingAgent';
import { fetchSavedItemCountsIfNeeded } from '@/redux/modules/savedItemCount';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getRegistrationBidLimit } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useNewBiddingConsoleExperiment } from '@/utils/amplitude/experiments/hooks/useNewBiddingConsole.experiment';
import DesktopBiddingConsole from '../DesktopBiddingConsole/DesktopBiddingConsole';
import LegacyMobileBiddingConsole from '@/pages/BiddingConsoleLegacy/layouts/MobileBiddingConsole/MobileBiddingConsole';
import LoadingScreen from '@/pages/BiddingConsole/components/LoadingScreen/LoadingScreen';
import styled from 'styled-components';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import useShowMobileConsole from '../../hooks/useShowMobileConsole';

type Props = {
    catalogId: number;
};

/**
 * Renders either the MobileBiddingConsole or DesktopBiddingConsole depending on screen size
 *
 * Persists the livestream video size when changing between the mobile + desktop consoles
 */
const ResponsiveBiddingConsole = ({ catalogId }: Props) => {
    // const [bigVideo, setBigVideo] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(true);

    const firstRender = React.useRef(true);

    // Get screen size to determine Desktop/Mobile console layout
    const showMobileConsole = useShowMobileConsole();

    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const agentId = useAppSelector((state) => getItemDetailAgentId(state, itemId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const prevBidLimit = usePrevious(bidLimit);
    const prevItemId = usePrevious(itemId);
    // NEW DESKTOP BIDDING CONSOLE EXPERIMENT
    const hasNewBiddingConsoleLayout = useNewBiddingConsoleExperiment();

    const dispatch = useAppDispatch();

    // If item changes, fetch new item
    React.useEffect(() => {
        if (!firstRender.current && prevItemId !== itemId) {
            dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, force: true, itemId }));
            dispatch(fetchItemDetailsIfNeeded([itemId]));
            // This is handling a weird edge case where if the catalog items call fails, we just have no items, and the console is broken
            dispatch(
                fetchItemSummaryIfNeeded({
                    identifier: 'bidding-console',
                    itemId,
                    liveStateFetch: true,
                })
            );
            dispatch(fetchSavedItemCountsIfNeeded([itemId]));

            if (agentId) {
                dispatch(fetchListingAgentsIfNeeded([agentId]));
            }
        }
    }, [agentId, catalogId, dispatch, itemId, prevItemId]);

    // Refetch bid limit balance if bid limit changes
    React.useEffect(() => {
        if (!firstRender.current && prevBidLimit !== bidLimit) {
            dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, itemId }));
        }
    }, [bidLimit, prevBidLimit, dispatch, catalogId, itemId]);

    React.useEffect(() => {
        firstRender.current = false;
    }, []);

    React.useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (isLoading) {
            timeout = setTimeout(() => setIsLoading(false), 2000);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [isLoading]);

    const transitions = useTransition(isLoading, {
        enter: { opacity: 1 },
        from: { opacity: 0 },
        leave: { opacity: 0 },
    });

    if (showMobileConsole) {
        return (
            <>
                {transitions(({ ...style }, item) =>
                    //Animation Note: transform: 'none' on this is to prevent position: fixed elements from breaking layout on mobile
                    item ? (
                        <StyledAnimatedDiv style={{ ...style, transform: 'none' }}>
                            <LoadingScreen />
                        </StyledAnimatedDiv>
                    ) : (
                        <StyledAnimatedDiv style={{ ...style, transform: 'none' }}>
                            <LegacyMobileBiddingConsole catalogId={catalogId} />
                        </StyledAnimatedDiv>
                    )
                )}
            </>
        );
    }
    return (
        <>
            {transitions(({ ...style }, item) =>
                item ? (
                    <StyledAnimatedDiv style={style}>
                        <LoadingScreen />
                    </StyledAnimatedDiv>
                ) : (
                    <StyledAnimatedDiv style={style}>
                        <div className="h-full w-full">
                            {hasNewBiddingConsoleLayout ? (
                                <DesktopBiddingConsoleExperiment catalogId={catalogId} />
                            ) : (
                                <DesktopBiddingConsole catalogId={catalogId} />
                            )}
                        </div>
                    </StyledAnimatedDiv>
                )
            )}
        </>
    );
};

export default ResponsiveBiddingConsole;

const StyledAnimatedDiv = styled(animated.div)`
    backface-visibility: hidden;
    -webkit-font-smoothing: subpixel-antialiased;
    /* Prevent transform from affecting scroll containers inside */
    will-change: opacity, transform;
`;
